import { camelCase } from "lodash";
import { InjectedFormProps, reduxForm } from "redux-form";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";

import {
  Button,
  NumericInputWrapperWithoutArrows,
  Separator,
  TextDisplay,
  Wrapper,
} from "src/styled";
import { createErrorNotification, createSuccessNotification, translate } from "src/services";
import { Input, TypedField } from "src/components";
import { sendCode } from "src/redux/reducers";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { isNumber } from "src/utils/validators";

export const VALIDATE_CODE_FORM_NAME = "validateCodeForm";

export interface ValidateCodeFormValues {
  code?: number;
  email: string;
  phone?: string;
  vendorURL: string;
}

interface PropsWithoutReduxForm {
  emailFromUrl?: string;
  phoneFromUrl?: string;
  vendorFromUrl?: string;
}

type Props = PropsWithoutReduxForm &
  InjectedFormProps<ValidateCodeFormValues, PropsWithoutReduxForm>;

const ValidateCodeForm = ({ handleSubmit, emailFromUrl, phoneFromUrl, vendorFromUrl }: Props) => {
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const {
    vendorURL: vendorUrl,
    user: { email, phone },
  } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  const [isResendCodeDisabled, setIsResendCodeDisabled] = useState(true);
  const [timer, setTimer] = useState(60);

  // on mount start timer for resend code button to be enabled
  useEffect(() => {
    const interval = setInterval(() => {
      if (timer === 0) {
        setIsResendCodeDisabled(false);
      } else {
        setTimer((prevTimer) => prevTimer - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const resendCode = useCallback(() => {
    if ((vendorUrl || vendorFromUrl) && (email || emailFromUrl || phone || phoneFromUrl)) {
      setTimer(60);
      setIsResendCodeDisabled(true);

      return sendCode({
        email: email || emailFromUrl,
        phone: phone || phoneFromUrl,
        vendorUrl: vendorUrl || vendorFromUrl,
      })(dispatch)
        .then((data) => {
          const { codeWasSent } = data;
          if (codeWasSent) {
            createSuccessNotification(
              translate("alertMessages.codeWasSentTo", {
                destination: email || emailFromUrl || phone || phoneFromUrl,
              })
            )(dispatch);
          } else {
            createErrorNotification(translate("alertMessages.generalError"))(dispatch);
          }
        })
        .catch((error) => {
          createErrorNotification(
            translate(
              error && error.response.data && error.response.data.code
                ? translate(`alertMessages.${camelCase(error.response.data.code)}`)
                : translate("alertMessages.generalError")
            )
          )(dispatch);
        });
    }
  }, [dispatch, email, phone, vendorUrl, vendorFromUrl, emailFromUrl, phoneFromUrl]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Wrapper padding="0 50px" maxWidth={500} margin="no auto">
        <NumericInputWrapperWithoutArrows>
          <TypedField
            component={Input}
            name="code"
            props={{
              type: "number",
              label: translate("common.verificationCode"),
              margin: "no auto",
              width: "80%",
            }}
            validate={emailFromUrl || phoneFromUrl || vendorFromUrl ? [] : [isNumber]}
          />
        </NumericInputWrapperWithoutArrows>
        <Separator height={50} />

        <Wrapper textAlign="center" maxWidth={380} margin="no auto">
          <Button
            type="submit"
            color="primary"
            size="medium"
            margin="sMedium auto"
            borderRadius="100px"
            loadingIndicator={isLoading}
          >
            {translate("common.continue")}
          </Button>

          {(email || emailFromUrl || phone || phoneFromUrl) && (
            <Wrapper textAlign="center">
              <TextDisplay block margin="small large">
                <em>{translate("loginPage.didNotReceiveCode")} </em>
                <Button
                  type="button"
                  color="primary"
                  text
                  disabled={isResendCodeDisabled}
                  onClick={resendCode}
                >
                  <em>
                    {translate("loginPage.resendCode")}{" "}
                    {!!timer && moment(timer, "s").isValid()
                      ? moment(timer, "s").format("mm:ss")
                      : ""}
                  </em>
                </Button>
              </TextDisplay>
            </Wrapper>
          )}
        </Wrapper>
      </Wrapper>
    </form>
  );
};

export default reduxForm<ValidateCodeFormValues, PropsWithoutReduxForm>({
  form: VALIDATE_CODE_FORM_NAME,
  enableReinitialize: true,
})(ValidateCodeForm);
